import { createContext, use } from "react";

const DARK_MODE_STORAGE_KEY = "darkMode";

interface DarkModeContextType {
  darkMode: boolean;
  setDarkMode: (darkMode: boolean) => void;
}

export const DarkModeContext = createContext<DarkModeContextType>({} as DarkModeContextType);

const parseStoredDarkMode = (storedValue: string | null) => {
  if (storedValue === null) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(storedValue);

    return typeof parsedValue === "boolean" ? parsedValue : null;
  } catch {
    return null;
  }
};

export const resolveDarkModePreference = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const storedValue = parseStoredDarkMode(window.localStorage.getItem(DARK_MODE_STORAGE_KEY));

  if (storedValue !== null) {
    return storedValue;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches;
};

export const applyDarkModeClass = (darkMode: boolean) => {
  if (typeof document === "undefined") {
    return;
  }

  document.documentElement.classList.toggle("dark", darkMode);
  document.documentElement.style.colorScheme = darkMode ? "dark" : "light";
};

export const setStoredDarkMode = (darkMode: boolean) => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(DARK_MODE_STORAGE_KEY, JSON.stringify(darkMode));
};

export const getDarkModeInitializationScript = () => `(function(){
  try {
    var storageKey = ${JSON.stringify(DARK_MODE_STORAGE_KEY)};
    var storedValue = window.localStorage.getItem(storageKey);
    var parsedValue = storedValue === null ? null : JSON.parse(storedValue);
    var darkMode = typeof parsedValue === "boolean"
      ? parsedValue
      : window.matchMedia("(prefers-color-scheme: dark)").matches;
    document.documentElement.classList.toggle("dark", darkMode);
    document.documentElement.style.colorScheme = darkMode ? "dark" : "light";
  } catch {
    var fallbackDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
    document.documentElement.classList.toggle("dark", fallbackDarkMode);
    document.documentElement.style.colorScheme = fallbackDarkMode ? "dark" : "light";
  }
})();`;

export const getStartingDarkModeValue = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return resolveDarkModePreference();
};

export const useDarkMode = () => use(DarkModeContext);
